import React, { useState } from 'react'
import {X, Wallet, Zap, DollarSign} from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '@/lib/lumi'

interface RegisterAgentModalProps {
  isOpen: boolean
  onClose: () => void
  onAgentRegistered: () => void
  userId?: string
}

const categories = ['Trading', 'Development', 'Research', 'Content', 'Data', 'Monitoring']
const pricingModels = ['per_task', 'per_hour', 'subscription']

export function RegisterAgentModal({ isOpen, onClose, onAgentRegistered, userId }: RegisterAgentModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    walletAddress: '',
    category: 'Development',
    capabilities: '',
    pricePerTask: '',
    pricingModel: 'per_task',
    responseTimeSLA: '< 5 min',
    apiEndpoint: '',
    supportedFormats: 'JSON, CSV',
    stakeAmount: '1000',
    bio: '',
    xHandle: ''
  })
  const [submitting, setSubmitting] = useState(false)

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!userId) {
      toast.error('Please login first')
      return
    }

    if (!formData.name || !formData.walletAddress || !formData.capabilities || !formData.pricePerTask || !formData.apiEndpoint) {
      toast.error('Please fill all required fields')
      return
    }

    setSubmitting(true)
    try {
      const agentData = {
        name: formData.name,
        walletAddress: formData.walletAddress,
        category: formData.category,
        capabilities: formData.capabilities.split(',').map(c => c.trim()).filter(Boolean),
        pricePerTask: parseFloat(formData.pricePerTask),
        pricingModel: formData.pricingModel,
        availability: 'online',
        responseTimeSLA: formData.responseTimeSLA,
        apiEndpoint: formData.apiEndpoint,
        supportedFormats: formData.supportedFormats.split(',').map(f => f.trim()).filter(Boolean),
        stakeAmount: parseFloat(formData.stakeAmount),
        bio: formData.bio || '',
        xHandle: formData.xHandle || '',
        rating: 0,
        tasksCompleted: 0,
        totalEarnings: 0,
        creator: userId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }

      await lumi.entities.agents.create(agentData)
      toast.success('Agent registered successfully!')
      onAgentRegistered()
      onClose()
      
      // Reset form
      setFormData({
        name: '',
        walletAddress: '',
        category: 'Development',
        capabilities: '',
        pricePerTask: '',
        pricingModel: 'per_task',
        responseTimeSLA: '< 5 min',
        apiEndpoint: '',
        supportedFormats: 'JSON, CSV',
        stakeAmount: '1000',
        bio: '',
        xHandle: ''
      })
    } catch (error: any) {
      console.error('Agent registration failed:', error)
      
      // Handle specific error messages
      if (error?.message?.includes('duplicate') || error?.message?.includes('already exists') || error?.message?.includes('already registered')) {
        if (error.message.includes('wallet')) {
          toast.error('This wallet address is already registered. Each wallet can only have one agent account.')
        } else if (error.message.includes('name')) {
          toast.error('This agent name is already taken. Please choose a different name.')
        } else {
          toast.error(error.message)
        }
      } else {
        toast.error('Failed to register agent. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-black border border-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-black border-b border-gray-800 p-6">
          <div className="flex justify-center mb-4">
            <img src="https://static.lumi.new/2d/2db01c8af5f8d23adc3535b856a32a22.webp" alt="XyberMart" className="h-12" />
          </div>
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white">Register as Agent</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Agent Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., TradingBot Alpha"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                <Wallet className="w-4 h-4" />
                Wallet Address *
              </label>
              <input
                type="text"
                value={formData.walletAddress}
                onChange={(e) => setFormData({ ...formData, walletAddress: e.target.value })}
                placeholder="0x..."
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-electric-purple-500 focus:outline-none font-mono text-sm"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-purple-500 focus:outline-none"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Pricing Model *
                </label>
                <select
                  value={formData.pricingModel}
                  onChange={(e) => setFormData({ ...formData, pricingModel: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-purple-500 focus:outline-none"
                >
                  {pricingModels.map((model) => (
                    <option key={model} value={model}>
                      {model.replace('_', ' ').toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Capabilities * (comma-separated)
              </label>
              <input
                type="text"
                value={formData.capabilities}
                onChange={(e) => setFormData({ ...formData, capabilities: e.target.value })}
                placeholder="e.g., Smart Contracts, Full Stack, Code Review"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Bio
              </label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Tell others about your agent's expertise and specialties..."
                rows={3}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                X Handle (Twitter)
              </label>
              <input
                type="text"
                value={formData.xHandle}
                onChange={(e) => setFormData({ ...formData, xHandle: e.target.value })}
                placeholder="@youragent or https://x.com/youragent"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">Optional: Let others follow you on X</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Price per Task (USD) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.pricePerTask}
                  onChange={(e) => setFormData({ ...formData, pricePerTask: e.target.value })}
                  placeholder="50"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Response SLA
                </label>
                <input
                  type="text"
                  value={formData.responseTimeSLA}
                  onChange={(e) => setFormData({ ...formData, responseTimeSLA: e.target.value })}
                  placeholder="< 5 min"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                API Endpoint *
              </label>
              <input
                type="url"
                value={formData.apiEndpoint}
                onChange={(e) => setFormData({ ...formData, apiEndpoint: e.target.value })}
                placeholder="https://api.youragent.com/v1"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Supported Formats (comma-separated)
              </label>
              <input
                type="text"
                value={formData.supportedFormats}
                onChange={(e) => setFormData({ ...formData, supportedFormats: e.target.value })}
                placeholder="JSON, CSV, PDF"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Stake Amount (Tokens)
              </label>
              <input
                type="number"
                value={formData.stakeAmount}
                onChange={(e) => setFormData({ ...formData, stakeAmount: e.target.value })}
                placeholder="1000"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">Minimum stake required to list your agent</p>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-gray-900 text-gray-300 rounded-lg font-semibold hover:bg-gray-800 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-700 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-purple-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Registering...' : 'Register Agent'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
